import React, { useRef, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL, getAuthHeaders } from '../../config/api.config';
import {
  zahlungErfassenMoeglich, parseBetrag, betragEingabe, pruefeZahlung, ZAHLUNG_ERFASSEN_HINWEIS, Zahlungsart,
} from '../../utils/zahlungErfassen';
import { ortsdatum } from '../../utils/kasse';

// E8/T-064 (v1.8.49, Session 41, R2 Option B): "Zahlung erfassen" in der Detailansicht einer Rechnung (bar oder Überweisung ohne
// Bankabgleich, z. B. Spesenrechnung über die Nachtragsrechnung). Verbucht wird serverseitig über den Weg des Bankabgleichs.

const fehlertext = (err: any, rueckfall: string): string => {
  const d = err?.response?.data;
  if (typeof d === 'string' && d) return d;
  return d?.message || d?.errorMessage || d?.error || err?.message || rueckfall;
};

export const ZahlungErfassenBereich: React.FC<{
  rechnung: { id: number; invoiceNumber: string; type: string; status: string; amountDue: number };
  onVerbucht: (hinweis: string) => void;
}> = ({ rechnung, onVerbucht }) => {
  const [offen, setOffen] = useState(false);
  const [betrag, setBetrag] = useState('');
  const [datum, setDatum] = useState('');
  const [art, setArt] = useState<Zahlungsart | ''>('');
  const [bemerkung, setBemerkung] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [hinweis, setHinweis] = useState<string | null>(null);
  const sperre = useRef(false);

  if (!zahlungErfassenMoeglich(rechnung)) return null;

  const oeffnen = () => {
    setBetrag(betragEingabe(rechnung.amountDue));
    setDatum(ortsdatum(new Date()));
    setArt('');
    setBemerkung('');
    setFehler(null);
    setHinweis(null);
    setOffen(true);
  };

  const verbuchen = async (trotzdem: boolean) => {
    if (sperre.current) return;
    const meldung = pruefeZahlung(betrag, datum, ortsdatum(new Date()), art);
    if (meldung) {
      setFehler(meldung);
      return;
    }
    sperre.current = true;
    setLaeuft(true);
    setFehler(null);
    try {
      const r = await axios.post(
        `${API_BASE_URL}/invoices/${rechnung.id}/zahlung-erfassen`,
        { betrag: parseBetrag(betrag), datum, zahlungsart: art, bemerkung: bemerkung.trim() || null, trotzdem },
        { headers: getAuthHeaders() }
      );
      setOffen(false);
      setHinweis(null);
      onVerbucht(`Rechnung ${rechnung.invoiceNumber}: ${r.data?.message || 'Zahlung verbucht'}.`);
    } catch (err: any) {
      if (err?.response?.status === 409) {
        setHinweis(fehlertext(err, 'Bitte die Zuordnung prüfen.'));
      } else {
        setFehler(fehlertext(err, 'Die Zahlung konnte nicht verbucht werden.'));
      }
    } finally {
      sperre.current = false;
      setLaeuft(false);
    }
  };

  return (
    <div className="detail-section" style={{ marginTop: '1rem', borderTop: '1px solid #e5e7eb', paddingTop: '1rem' }}>
      {!offen ? (
        <button className="btn-secondary" onClick={oeffnen} title="Zahlung ohne Bankabgleich verbuchen (bar, Überweisung oder PayPal)">
          💶 Zahlung erfassen
        </button>
      ) : (
        <div>
          <h3>Zahlung erfassen, Rechnung {rechnung.invoiceNumber}</h3>
          <p className="section-hint">{ZAHLUNG_ERFASSEN_HINWEIS}</p>
          <div className="form-group">
            <label htmlFor="zeArt">Zahlungsart *</label>
            <select id="zeArt" className="form-input" value={art} onChange={(e) => { setArt(e.target.value as Zahlungsart | ''); setHinweis(null); }} disabled={laeuft}>
              <option value="">Bitte wählen</option>
              <option value="Bar">Bar</option>
              <option value="Überweisung">Überweisung</option>
              <option value="PayPal">PayPal (nach Freigabe durch PayPal)</option>
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="zeBetrag">Betrag in EUR * (offen {betragEingabe(rechnung.amountDue)})</label>
            <input id="zeBetrag" className="form-input" type="text" inputMode="decimal" value={betrag}
              onChange={(e) => { setBetrag(e.target.value); setHinweis(null); }} disabled={laeuft} />
          </div>
          <div className="form-group">
            <label htmlFor="zeDatum">Zahlungsdatum *</label>
            <input id="zeDatum" className="form-input" type="date" value={datum} max={ortsdatum(new Date())}
              onChange={(e) => { setDatum(e.target.value); setHinweis(null); }} disabled={laeuft} />
          </div>
          <div className="form-group">
            <label htmlFor="zeBemerkung">Bemerkung (optional)</label>
            <input id="zeBemerkung" className="form-input" type="text" maxLength={200} value={bemerkung}
              onChange={(e) => { setBemerkung(e.target.value); setHinweis(null); }} placeholder="z. B. an Bord kassiert, Floß 2" disabled={laeuft} />
          </div>
          {fehler && <div className="error-message" style={{ whiteSpace: 'pre-line' }}>{fehler}</div>}
          {hinweis && (
            <div className="error-message" style={{ whiteSpace: 'pre-line' }}>
              {hinweis}
              <div style={{ marginTop: '0.5rem' }}>
                <button className="btn-secondary" onClick={() => verbuchen(true)} disabled={laeuft}>
                  Trotzdem verbuchen
                </button>
              </div>
            </div>
          )}
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
            <button className="btn-primary" onClick={() => verbuchen(false)} disabled={laeuft || !art}>
              {laeuft ? 'Wird verbucht ...' : 'Zahlung verbuchen'}
            </button>
            <button className="btn-secondary" onClick={() => { setOffen(false); setFehler(null); setHinweis(null); }} disabled={laeuft}>
              Abbrechen
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
